<script lang="ts" setup>
import ThreadsNav from "@/components/mobile/ThreadsNav.vue";
import { computed, onBeforeMount, ref } from "vue";
import { useRoute, useRouter } from "vue-router";
import { useStory } from "@/stores/story";
import { useMate } from "@/stores/mate";
import StoryCard from "@/components/mobile/story/story-chapter/StoryCard.vue";
import ReplyList from "@/components/mobile/reply/ReplyList.vue";
import ReplyInput, {
  ReplyInputInstance,
} from "@/components/mobile/reply/ReplyInput.vue";
import MomentActions from "@/components/mobile/popup/MomentActions.vue";
import HappyFaceIcon from "@/components/icon/HappyFaceIcon.vue";
import EmojiVotePopup from "@/components/mobile/popup/EmojiVotePopup.vue";
import ReactionCounter from "@/components/mobile/reply/ReactionCounter.vue";
import { useStoryBook } from "@/stores/story-book";
import { ReplyEntityType } from "@/enums";

const loading = ref(false);
const route = useRoute();
const router = useRouter();
const storyStore = useStory();
const storyBookStore = useStoryBook();
const mateStore = useMate();
const author = ref<Mate | null>(null);
const storyBook = computed(() => {
  if (!route.params.uuid) {
    return undefined;
  }
  return storyBookStore.storyBookMap[route.params.uuid as string];
});
// 底部输入框状态
const replyTarget = ref<Reply | null>(null);
const replyInputRef = ref<ReplyInputInstance | null>(null);
const showActions = ref(false);
const toOperateReply = ref<Reply | null>(null);
const showEmoji = ref(false);

const prepareData = async () => {
  loading.value = true;
  try {
    const uuid: string = route.params.uuid as string;
    await storyBookStore.getOrFetchStoryItem(uuid);
    // Story.text若为plainText，则转换为JSON并将Moment中的图片插入到JSON中
    author.value = await mateStore.getOrFetchMate(storyBook.value!.mate.uuid);
    replyTarget.value = storyBook.value!.reply;
    replyTarget.value.mate = author.value;
  } finally {
    loading.value = false;
  }
};
onBeforeMount(() => {
  prepareData();
});

const onReplyBtnClick = (replay: Reply) => {
  replyTarget.value = replay;
  replyInputRef.value?.focus();
};
const onReplyActionClick = (reply: Reply) => {
  toOperateReply.value = reply;
  showActions.value = true;
};
</script>
<template>
  <div class="h-full grid grid-rows-[auto_1fr_auto] relative">
    <threads-nav :enable-switch-mate="false" :show-back="true" />
    <div v-if="loading" class="w-full h-full flex items-center justify-center">
      <span class="loading"></span>
    </div>
    <div
      v-else-if="storyBook && storyBook.reply"
      class="h-full overflow-y-auto"
    >
      <story-card
        :storyBook="storyBook"
        :cover-url="storyBook!.media?.url"
        :desc="storyBook?.description"
        :dt-publish="storyBook!.dtPublish"
        :mate="author!"
        :show-statistics="false"
        :title="storyBook!.title"
        :view-count="storyBook?.reply.viewerCount"
        :visibility="storyBook?.visibility"
        @on-card-click="
          router.push({
            name: 'storyInfo',
            params: { uuid: storyBook!.uuid },
          })
        "
      >
        <template #button-group>
          <reaction-counter :reply="storyBook?.reply" />
          <button
            v-if="mateStore.currentMate"
            class="btn btn-xs btn-neutral rounded"
            @click="
              showEmoji = true;
              toOperateReply = storyBook?.reply!;
            "
          >
            <happy-face-icon class="icon-sm" />
          </button>
        </template>
      </story-card>
      <div>
        <div class="font-bold text-base p-4">
          {{ $t("Texts.allComments") }} {{ storyBook!.reply.replyCount }}
        </div>
        <reply-list
          :root-id="storyBook!.reply.id"
          class="px-2"
          @on-reply-click="onReplyBtnClick"
          @on-reaction-click="onReplyActionClick"
        />
      </div>
    </div>
    <reply-input
      v-if="!loading && storyBook"
      ref="replyInputRef"
      :reply-target="replyTarget"
      :entity-type="ReplyEntityType.StoryBook"
      :entity="storyBook"
    />
    <moment-actions v-model:show="showActions" :reply="toOperateReply" />
    <emoji-vote-popup
      v-model:show="showEmoji"
      :to-vote-reply="toOperateReply"
    />
  </div>
</template>
